/* eslint-disable react/jsx-props-no-spreading */
import React, { useEffect, useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  Button,
  Text,
  FormControl,
  FormLabel,
  InputGroup,
  Input,
  InputRightElement,
  Flex,
  Image,
  RadioGroup,
  Stack,
  Radio,
  Box,
  Icon,
  Tooltip,
  ModalCloseButton,
} from '@chakra-ui/react';
import { HouseSimple, ListChecks, Wind } from '@phosphor-icons/react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import WarehouseImage from '../../../../assets/warehouse.png';
import WareHouseAltura from '../../../../assets/wareHouseAltura.png';
import WareHouseLargura from '../../../../assets/wareHouseLargura.png';
import distanciaPortico from '../../../../assets/distanciaPortico.png';
import windBrazilMap from '../../../../assets/wind.png';
import { DistanceViewIcon } from '../../../../assets/Icons/DistanceView.component';
import {
  ParametersData,
  ParametersImageProps,
  ParametersModalProps,
  ParametersSchema,
} from './ParametersModal.types';
import { useQuantitatives } from '../../../../contexts/QuantitativesContext';
import { CustomIcon } from '../../../CustomIcon/CustomIcon.component';
import { WarningAlertIcon } from '../../../Icons';

export function ParametersModal({ isOpen, onClose, onSubmit, onReturn }: ParametersModalProps) {
  const { measures, setMeasures, setAlertModalIsOpen, userData } = useQuantitatives();
  const [parametersImage, setParametersImage] = useState<ParametersImageProps>({
    image: WarehouseImage,
    iconSelected: 'HouseSimple',
  });
  const {
    register,
    handleSubmit,
    setFocus,
    getValues,
    formState: { errors },
  } = useForm<ParametersData>({
    resolver: zodResolver(ParametersSchema(measures.comprimento)),
    mode: 'onChange',
  });

  const onSaveData: SubmitHandler<ParametersData> = async (data) => {
    const payload = {
      ...data,
      vaoPortico: measures.largura,
      comprimentoArea: measures.comprimento,
      larguraArea: measures.largura,
      nome: userData?.nome,
      email: userData?.email,
      profissao: userData?.profissao,
    };
    setMeasures((prevState) => ({
      ...prevState,
      altura: data.alturaColuna,
      distanciaPort: data.distanciaPortico,
    }));
    onSubmit(payload);
  };

  const handleFocusIconHouseSimple = () => {
    const alturaColunaWatch = getValues('alturaColuna');
    if (alturaColunaWatch) {
      return setFocus('vaoPortico');
    }
    return setFocus('alturaColuna');
  };

  const iconColors: Record<string, string> = {
    HouseSimple: parametersImage.iconSelected === 'HouseSimple' ? '#022D58' : '#B3B3B3',
    DistanceView: parametersImage.iconSelected === 'DistanceView' ? '#022D58' : '#B3B3B3',
    Wind: parametersImage.iconSelected === 'Wind' ? '#022D58' : '#B3B3B3',
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        setAlertModalIsOpen({
          open: true,
          submitDangerous: false,
          title: 'Modificações não salvas',
          description: 'Ao cancelar, você perderá todo o progresso realizado.',
          icon: WarningAlertIcon,
          textSubmit: 'Continuar',
          textCancel: 'Cancelar',
          onSubmit: () => {
            onClose();
          },
        });
      }}
      isCentered
    >
      <ModalOverlay />
      <ModalContent maxWidth="35rem">
        <ModalHeader>
          <Flex alignItems="center" justifyContent="space-between">
            <CustomIcon
              icon={ListChecks}
              boxSize={6}
              color="#B3B3B3"
              $bgColor="#F2F4F7"
              $borderColor="#F9FAFB"
              $isRounded
            />
            <ModalCloseButton position="unset" border="1px solid #CBD5E0" />
          </Flex>
          <Text color="#1D1D1B" fontSize="18px" mt="1rem">
            Definir parâmetros
          </Text>
          <Text fontWeight="400" color="#767B7B" fontSize="14px" mt="0.5rem">
            Insira os valores de cada parâmetro que vão servir de referência para calcular as
            quantidades necessárias.
          </Text>
        </ModalHeader>

        <ModalBody display="flex" justifyContent="space-between" flexDirection="row">
          <Flex
            as="form"
            id="parametersForm"
            flexDirection="column"
            gap="1rem"
            onSubmit={handleSubmit(onSaveData)}
          >
            <FormControl isReadOnly>
              <FormLabel display="flex">
                <Tooltip label="O vão do pórtico é igual à largura total do galpão">
                  <Flex>
                    <Text fontSize="14px" color="#2D3748">
                      Vão do pórtico
                    </Text>
                    <Text fontSize="14px" color="#767B7B">
                      &nbsp;(L)
                    </Text>
                  </Flex>
                </Tooltip>
              </FormLabel>
              <InputGroup w="7.5rem">
                <Input
                  type="number"
                  step="0.01"
                  placeholder=""
                  textAlign="end"
                  value={measures.largura}
                  bgColor="#F4F4F4"
                  onFocus={() =>
                    setParametersImage({
                      image: WareHouseLargura,
                      iconSelected: 'HouseSimple',
                    })
                  }
                  {...register('vaoPortico', {
                    valueAsNumber: true,
                  })}
                />
                <InputRightElement>
                  <Text fontSize="14px">
                    m <span style={{ color: '#C53030' }}>*</span>
                  </Text>
                </InputRightElement>
              </InputGroup>
              {errors.vaoPortico && (
                <Text fontSize="12px" color="red">
                  {errors.vaoPortico.message}
                </Text>
              )}
            </FormControl>
            <FormControl>
              <FormLabel display="flex">
                <Text fontSize="14px" color="#2D3748">
                  Altura da coluna
                </Text>
                <Text fontSize="14px" color="#767B7B">
                  &nbsp;(H)
                </Text>
              </FormLabel>
              <InputGroup w="7.5rem">
                <Input
                  type="number"
                  step="0.01"
                  placeholder=""
                  textAlign="end"
                  onFocus={() =>
                    setParametersImage({
                      image: WareHouseAltura,
                      iconSelected: 'HouseSimple',
                    })
                  }
                  {...register('alturaColuna', {
                    valueAsNumber: true,
                  })}
                />
                <InputRightElement>
                  <Text fontSize="14px">
                    m <span style={{ color: '#C53030' }}>*</span>
                  </Text>
                </InputRightElement>
              </InputGroup>
              {errors.alturaColuna && (
                <Text fontSize="12px" color="red">
                  {errors.alturaColuna.message}
                </Text>
              )}
            </FormControl>
            <FormControl>
              <FormLabel display="flex">
                <Text fontSize="14px" color="#2D3748">
                  Distância entre pórticos
                </Text>
                <Text fontSize="14px" color="#767B7B">
                  &nbsp;(B)
                </Text>
              </FormLabel>
              <InputGroup w="7.5rem">
                <Input
                  type="number"
                  step="0.0001"
                  placeholder=""
                  textAlign="end"
                  onFocus={() =>
                    setParametersImage({
                      image: distanciaPortico,
                      iconSelected: 'DistanceView',
                    })
                  }
                  {...register('distanciaPortico', {
                    valueAsNumber: true,
                  })}
                />
                <InputRightElement>
                  <Text fontSize="14px">
                    m <span style={{ color: '#C53030' }}>*</span>
                  </Text>
                </InputRightElement>
              </InputGroup>
              {errors.distanciaPortico && (
                <Text fontSize="12px" color="red">
                  {errors.distanciaPortico.message}
                </Text>
              )}
            </FormControl>
            <FormControl>
              <FormLabel display="flex">
                <Text fontSize="14px" color="#2D3748">
                  Velocidades do vento
                </Text>
              </FormLabel>
              <InputGroup w="7.5rem">
                <Input
                  type="number"
                  step="0.01"
                  placeholder=""
                  textAlign="end"
                  onFocus={() =>
                    setParametersImage({
                      image: windBrazilMap,
                      iconSelected: 'Wind',
                    })
                  }
                  {...register('velocidadeVento', {
                    valueAsNumber: true,
                  })}
                />
                <InputRightElement>
                  <Text fontSize="14px">
                    m/s <span style={{ color: '#C53030' }}>*</span>
                  </Text>
                </InputRightElement>
              </InputGroup>
              {errors.velocidadeVento && (
                <Text fontSize="12px" color="red">
                  {errors.velocidadeVento.message}
                </Text>
              )}
            </FormControl>
            <FormControl>
              <RadioGroup margin="auto 0 0" defaultValue="simples">
                <Text color="#1D1D1B" fontSize="14px">
                  Tipologia
                </Text>
                <Stack spacing={1} direction="column">
                  <Radio variant="primary" value="simples" isChecked {...register('tipologia')}>
                    <Text fontSize="14px">Simples</Text>
                  </Radio>
                </Stack>
              </RadioGroup>
              {errors.tipologia && (
                <Text fontSize="12px" color="red">
                  {errors.tipologia.message}
                </Text>
              )}
            </FormControl>
          </Flex>

          <Flex
            w="277px"
            flexDir="column"
            borderRadius="0.5rem"
            border="1px solid #B3B3B3"
            padding="1.5rem"
          >
            <Box margin="auto">
              <Image objectFit="cover" src={parametersImage.image} alt="" />
            </Box>

            <Flex justifyContent="space-between" margin="auto 0 0">
              <Icon
                as={HouseSimple}
                color={iconColors.HouseSimple}
                w="26px"
                h="26px"
                cursor="pointer"
                onClick={() => {
                  setParametersImage({
                    image: WarehouseImage,
                    iconSelected: 'HouseSimple',
                  });
                  handleFocusIconHouseSimple();
                }}
              />
              <DistanceViewIcon
                color={iconColors.DistanceView}
                onClick={() => {
                  setParametersImage({
                    image: distanciaPortico,
                    iconSelected: 'DistanceView',
                  });
                  setFocus('distanciaPortico');
                }}
              />
              <Icon
                as={Wind}
                cursor="pointer"
                color={iconColors.Wind}
                w="26px"
                h="26px"
                onClick={() => {
                  setParametersImage({
                    image: windBrazilMap,
                    iconSelected: 'Wind',
                  });
                  setFocus('velocidadeVento');
                }}
              />
            </Flex>
          </Flex>
        </ModalBody>

        <ModalFooter mt="1rem">
          <Button flex="1" variant="tertiary" mr={3} onClick={onReturn}>
            Voltar
          </Button>
          <Button flex="1" variant="quaternary" type="submit" form="parametersForm">
            Continuar
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
